"use client";

import React, { useContext, useState } from "react";
import { twMerge } from "tailwind-merge";

import CalendarArrow from "@/app/(main)/(club)/mes-clubs/[id]/components/calendar-arrow";
import CalendarHeaderDays from "@/app/(main)/(club)/mes-clubs/[id]/components/calendar-header-days";
import CalendarLegend from "@/app/(main)/(club)/mes-clubs/[id]/components/calendar-legend";
import {
  ALREADY_INVITED_LEGEND,
  ALREADY_INVITED_LEGEND_COLOR,
  ALREADY_INVITED_TOOLTIP,
  isDayAlreadyInvited,
  useAlreadyInvitedKeys,
} from "@/app/(main)/(club)/mes-clubs/[id]/utils/already-invited-dates";
import { getFirstDayOfMonth } from "@/app/(main)/(club)/mes-clubs/[id]/utils/utils";
import { ClubContext } from "@/context/club-provider";
import { EventForCalendar } from "@/TS/Interfaces/global_interfaces";

export default function DatePickerElement({
  date,
  handleMonthDate,
  allDaysOfMonth,
  isLoading,
  handleEvent,
  eventArr,
  displayEventArr,
  className = "",
  alreadyInvitedDates,
}: {
  date: Date;
  handleMonthDate: (increment: number) => unknown;
  allDaysOfMonth: number[];
  isLoading: boolean;
  handleEvent: (index: number) => void;
  eventArr: EventForCalendar[];
  displayEventArr: (number | Date)[];
  className?: string;
  colorFormat?: string;
  alreadyInvitedDates?: string[];
}) {
  const [currentDate] = useState<Readonly<Date>>(() => new Date());
  const { format } = useContext(ClubContext);

  // Dates où la personne est déjà invitée (non sélectionnables).
  const alreadyInvitedKeys = useAlreadyInvitedKeys(alreadyInvitedDates);
  const isAlreadyInvited = (index: number): boolean =>
    isDayAlreadyInvited(alreadyInvitedKeys, date, index + 1);

  const hasEventPassed = (index: number): boolean | undefined => {
    if (eventArr.length === 0) return;
    const actualDate = eventArr.filter(
      (res) => res.date.getDate() === index + 1,
    );
    if (actualDate.length === 0) return;
    const eventEndOfDay = new Date(
      actualDate[0]?.date.getFullYear(),
      actualDate[0]?.date.getMonth(),
      actualDate[0]?.date.getDate(),
      23,
      59,
      59,
    );
    return eventEndOfDay < currentDate;
  };

  const isEventCanceled = (index: number) => {
    if (!eventArr || eventArr?.length === 0 || displayEventArr.length === 0)
      return;

    const event = eventArr.filter((res) => res.date.getDate() === index + 1);
    if (event) return event[0]?.cancel;
  };
  return (
    <div
      className={twMerge(
        `flex flex-col z-50 h-fit rounded-lg bg-white ring-1 ring-secondary w-full max-w-[400px] p-6 self-center md:self-start md:max-w-[300px] relative ${className}`,
      )}
    >
      <div className="head flex justify-between items-center">
        <CalendarArrow onClick={() => handleMonthDate(-1)} isLeft={true} />
        <p className={"capitalize text-lg text-nowrap w-full"}>
          {date.toLocaleDateString(undefined, {
            month: "long",
          })}{" "}
          <span>
            {date.toLocaleDateString(undefined, {
              year: "numeric",
            })}
          </span>
        </p>
        <CalendarArrow onClick={() => handleMonthDate(1)} />
      </div>
      <CalendarHeaderDays />
      <div className="grid grid-cols-7 text-center gap-y-2 mb-3 auto-rows-auto">
        {getFirstDayOfMonth(date).map((res, index) => (
          <p key={index}></p>
        ))}
        {allDaysOfMonth.map((res, index) => {
          if (isLoading) {
            return (
              <div
                key={index}
                className="text-lg aspect-square flex items-center justify-center bg-dark-grey rounded-full text-white m-1 ease-in-out transition-all animate-pulse"
                style={{
                  animationDelay: `${Math.round(Math.random() * 1500)}ms`,
                }}
              >
                <p></p>
              </div>
            );
          }
          const dayAlreadyInvited = isAlreadyInvited(index);
          return (
            <div
              onClick={() => {
                if (isEventCanceled(index) || dayAlreadyInvited) return;
                handleEvent(index + 1);
              }}
              key={index}
              title={dayAlreadyInvited ? ALREADY_INVITED_TOOLTIP : undefined}
              className={`text-lg aspect-square flex items-center justify-center rounded-full ${
                dayAlreadyInvited ? "cursor-not-allowed" : "cursor-pointer"
              } ${
                displayEventArr.indexOf(index + 1) !== -1
                  ? dayAlreadyInvited
                    ? "bg-dark-grey text-white opacity-70" // already invited
                    : isEventCanceled(index)
                      ? "bg-event bg-opacity-50 text-white" // if event is canceled
                      : hasEventPassed(index)
                        ? "bg-[lightgray] text-white font-bold" // if event is passed and not canceled
                        : `${format?.bgColor ?? "bg-event"} font-bold text-white` // if event is coming
                  : "text-secondary hover:bg-hover-grey" // if no event
              }`}
            >
              {index + 1}
            </div>
          );
        })}
      </div>
      <CalendarLegend colorFormat={format?.bgColor ?? "bg-event"} />
      {alreadyInvitedKeys.size > 0 && (
        <CalendarLegend
          colorFormat={ALREADY_INVITED_LEGEND_COLOR}
          content={ALREADY_INVITED_LEGEND}
        />
      )}
    </div>
  );
}
